import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { UserPlus, Trash2, Pencil, RefreshCw, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTIES = [
  { id: "GTHR", name: "Golden Tulip Holland Resort" },
  { id: "GH", name: "Golden Hill" }
];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    name: "",
    role: "",
    department: "",
    property_id: "GTHR",
    email: "",
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editUser, setEditUser] = useState({
    username: "",
    name: "",
    role: "",
    department: "",
    property_id: "GTHR",
    email: "",
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, {
        ...newUser,
        department: newUser.department || null,
        email: newUser.email || null,
      });
      toast.success("User created successfully");
      setDialogOpen(false);
      setNewUser({ username: "", password: "", name: "", role: "", department: "", property_id: "GTHR", email: "" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
  };

  const handleOpenEdit = (user) => {
    setEditingUserId(user.id);
    setEditUser({
      username: user.username,
      name: user.name,
      role: user.role,
      department: user.department || "",
      property_id: user.properties?.[0] || "GTHR",
      email: user.email || "",
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/users/${editingUserId}`, {
        ...editUser,
        department: editUser.role === "HOD" ? (editUser.department || null) : null,
        email: editUser.email || null,
      });
      toast.success("User updated successfully");
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const roleColors = {
    GM: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    DM: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    HOD: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    FO: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">
              User Management
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {users.length} registered users
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchUsers} variant="outline" size="sm" data-testid="refresh-users">
              <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-primary" data-testid="add-user-btn">
                  <UserPlus size={14} className="mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase tracking-wider">
                    Create New User
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Full Name
                    </Label>
                    <Input
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      className="bg-background"
                      data-testid="new-user-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Username
                    </Label>
                    <Input
                      type="text"
                      value={newUser.username}
                      onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                      placeholder="username"
                      required
                      className="bg-background"
                      data-testid="new-user-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Password
                    </Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="bg-background"
                      data-testid="new-user-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Role
                    </Label>
                    <Select
                      value={newUser.role}
                      onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                      required
                    >
                      <SelectTrigger className="bg-background" data-testid="new-user-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FO">Front Office (FO)</SelectItem>
                        <SelectItem value="HOD">Head of Department (HOD)</SelectItem>
                        <SelectItem value="DM">Duty Manager (DM)</SelectItem>
                        <SelectItem value="GM">General Manager (GM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newUser.role === "HOD" && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Department
                      </Label>
                      <Select
                        value={newUser.department}
                        onValueChange={(value) => setNewUser({ ...newUser, department: value })}
                      >
                        <SelectTrigger className="bg-background" data-testid="new-user-department">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ENG">Engineering</SelectItem>
                          <SelectItem value="HK">Housekeeping</SelectItem>
                          <SelectItem value="FNB">F&B</SelectItem>
                          <SelectItem value="FO">Front Office</SelectItem>
                          <SelectItem value="SEC">Security</SelectItem>
                          <SelectItem value="IT">IT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {newUser.role === "HOD" && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Email (for notifications)
                      </Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="name@goldentulipbatu.com"
                        className="bg-background"
                        data-testid="new-user-email"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Property
                    </Label>
                    <Select
                      value={newUser.property_id}
                      onValueChange={(value) => setNewUser({ ...newUser, property_id: value })}
                      required
                    >
                      <SelectTrigger className="bg-background" data-testid="new-user-property">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTIES.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    data-testid="create-user-submit"
                  >
                    Create User
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase tracking-wider">
                    Edit User
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdateUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Full Name
                    </Label>
                    <Input
                      value={editUser.name}
                      onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                      required
                      className="bg-background"
                      data-testid="edit-user-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Username
                    </Label>
                    <Input
                      type="text"
                      value={editUser.username}
                      onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
                      required
                      className="bg-background"
                      data-testid="edit-user-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Role
                    </Label>
                    <Select
                      value={editUser.role}
                      onValueChange={(value) => setEditUser({ ...editUser, role: value })}
                      required
                    >
                      <SelectTrigger className="bg-background" data-testid="edit-user-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FO">Front Office (FO)</SelectItem>
                        <SelectItem value="HOD">Head of Department (HOD)</SelectItem>
                        <SelectItem value="DM">Duty Manager (DM)</SelectItem>
                        <SelectItem value="GM">General Manager (GM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editUser.role === "HOD" && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Department
                      </Label>
                      <Select
                        value={editUser.department}
                        onValueChange={(value) => setEditUser({ ...editUser, department: value })}
                      >
                        <SelectTrigger className="bg-background" data-testid="edit-user-department">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ENG">Engineering</SelectItem>
                          <SelectItem value="HK">Housekeeping</SelectItem>
                          <SelectItem value="FNB">F&B</SelectItem>
                          <SelectItem value="FO">Front Office</SelectItem>
                          <SelectItem value="SEC">Security</SelectItem>
                          <SelectItem value="IT">IT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {editUser.role === "HOD" && (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Email (for notifications)
                      </Label>
                      <Input
                        type="email"
                        value={editUser.email}
                        onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                        placeholder="name@goldentulipbatu.com"
                        className="bg-background"
                        data-testid="edit-user-email"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Property
                    </Label>
                    <Select
                      value={editUser.property_id}
                      onValueChange={(value) => setEditUser({ ...editUser, property_id: value })}
                      required
                    >
                      <SelectTrigger className="bg-background" data-testid="edit-user-property">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTIES.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    data-testid="update-user-submit"
                  >
                    Save Changes
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Users List */}
        <Card className="orbit-card">
          <CardHeader>
            <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <UsersIcon size={16} />
              Registered Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : users.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No users found</p>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 bg-surface-highlight/50 rounded-sm border border-border"
                    data-testid={`user-row-${user.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-surface-highlight rounded-full flex items-center justify-center text-sm font-bold">
                        {user.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.username}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={roleColors[user.role]}>
                        {user.role}
                      </Badge>
                      {user.department && (
                        <Badge variant="outline" className="text-xs">
                          {user.department}
                        </Badge>
                      )}
                      {user.role === "HOD" && !user.email && (
                        <Badge className="text-xs bg-amber-500/10 text-amber-500 border-amber-500/20">
                          Email not set
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        {PROPERTIES.find((p) => p.id === user.properties?.[0])?.name || user.properties?.[0]}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(user)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Users;
